// @flow
import { isPlaywright } from 'utils/isPlaywright';
import { env } from './env';

type Config = {
  deployEnvironment: string,
  graphql: string,
  graphqlWs: string,
  apiGateway: string,
  pixelLoaderWs: string,
  nvoqWs: string,
  nvoqWsMaximumConnections: number,
  asrPlexWs: string,
  segmentAppId: string,
  splitioApiKey: string,
};

type EnvMapping = {
  ['local' | 'development' | 'staging' | 'production']: Config,
  preview: (string) => Config, // Special case to allow namespaced previews
  sironaPreview: (string) => Config, // Special case to allow namespaced previews
};

const OKTETO_NAMESPACE = env.OKTETO_NAMESPACE || '';
export const SIRONA_PREVIEW: string = env.SIRONA_PREVIEW || '';
const envMapping: EnvMapping = {
  local: {
    deployEnvironment: 'local',
    graphql: `http://localhost:4000/graphql`,
    graphqlWs: `ws://localhost:4000/graphql`,
    apiGateway: 'http://localhost:4000',
    pixelLoaderWs: `ws://localhost:4000/pixelws`,
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    asrPlexWs: 'ws://localhost:4000/ws/transcribe',
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    // Playwright uses the local env config which is probably incorrect, but for now
    // we need to accomodate and preserve the splitio api key for e2e tests.
    splitioApiKey:
      env.NODE_ENV === 'test'
        ? 'localhost'
        : isPlaywright() || env.LOCAL_SPLITS !== 'true'
        ? 'ulc2iijia2jp42b2mf1l600k61c42iufnmav'
        : 'localhost',
  },
  preview: (oktetoNamespace: string) => ({
    deployEnvironment: 'preview',
    graphql: `https://graphql-${oktetoNamespace}.preview.dev.sironamedical.com/graphql`,
    graphqlWs: `wss://graphql-${oktetoNamespace}.preview.dev.sironamedical.com/graphql`,
    apiGateway: `https://graphql-${oktetoNamespace}.preview.dev.sironamedical.com`,
    pixelLoaderWs: `wss://graphql-${oktetoNamespace}.preview.dev.sironamedical.com/pixelws`,
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    asrPlexWs: `wss://graphql-${oktetoNamespace}.preview.dev.sironamedical.com/ws/transcribe`,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  }),
  sironaPreview: (sironaPreview: string) => ({
    deployEnvironment: 'preview',
    graphql: `https://graphql-${sironaPreview}.dev.sironamedical.com/graphql`,
    graphqlWs: `wss://graphql-${sironaPreview}.dev.sironamedical.com/graphql`,
    apiGateway: `https://graphql-${sironaPreview}.dev.sironamedical.com`,
    pixelLoaderWs: `wss://graphql-${sironaPreview}.dev.sironamedical.com/pixelws`,
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    asrPlexWs: `wss://graphql-${sironaPreview}.dev.sironamedical.com/ws/transcribe`,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  }),
  development: {
    deployEnvironment: 'development',
    graphql: 'https://graphql.dev.sironamedical.com/graphql',
    graphqlWs: 'wss://graphql.dev.sironamedical.com/graphql',
    apiGateway: 'https://graphql.dev.sironamedical.com',
    pixelLoaderWs: 'wss://graphql.dev.sironamedical.com/pixelws',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    asrPlexWs: 'wss://graphql.dev.sironamedical.com/ws/transcribe',
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  },
  staging: {
    deployEnvironment: 'staging',
    graphql: 'https://graphql.stg.sironamedical.com/graphql',
    graphqlWs: 'wss://graphql.stg.sironamedical.com/graphql',
    apiGateway: 'https://graphql.stg.sironamedical.com',
    pixelLoaderWs: 'wss://graphql.stg.sironamedical.com/pixelws',
    nvoqWs: 'wss://healthcare.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 7,
    asrPlexWs: 'wss://graphql.stg.sironamedical.com/ws/transcribe',
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: '69fu9r39ra5d4914kd7g27p246i5gsvhn3ko',
  },
  production: {
    deployEnvironment: 'production',
    graphql: 'https://graphql.prod.sironamedical.com/graphql',
    graphqlWs: 'wss://graphql.prod.sironamedical.com/graphql',
    apiGateway: 'https://graphql.prod.sironamedical.com',
    pixelLoaderWs: 'wss://graphql.prod.sironamedical.com/pixelws',
    nvoqWs: 'wss://healthcare.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 7,
    asrPlexWs: 'wss://graphql.prod.sironamedical.com/ws/transcribe',
    segmentAppId: 'PAwhfXbi6zGSCvgr5I8hC9kF3ZphVj0g',
    splitioApiKey: 'behco42hidesjj8f2n86lco36qvui88fvi7s',
  },
};

const getConfigFromDeployEnv = () => {
  switch (env.DEPLOY_ENV) {
    case 'production':
      return envMapping.production;
    case 'staging':
      return envMapping.staging;
    case 'development':
      return envMapping.development;
    case 'preview':
      return SIRONA_PREVIEW
        ? envMapping.sironaPreview(SIRONA_PREVIEW)
        : envMapping.preview(OKTETO_NAMESPACE);
    default:
      return envMapping.local;
  }
};

const config: Config = getConfigFromDeployEnv();

// If a environment variable is provided, we try to parse and use it
const envOrFallback = <T>(env: ?string | T, fallback: T): T => {
  if (env != null) {
    if (typeof env === 'string') {
      try {
        return JSON.parse(env);
      } catch (e) {
        // $FlowIgnore[incompatible-return] assume a string is good
        return env;
      }
    } else {
      return env;
    }
  }
  return fallback;
};

export const API_GATEWAY_URL: string = envOrFallback(env.API_GATEWAY_URL, config.apiGateway);
export const GRAPHQL_URL: string = envOrFallback(env.GRAPHQL_ENDPOINT, config.graphql);
export const GRAPHQL_WS_URL: string = envOrFallback(env.GRAPHQL_WS_ENDPOINT, config.graphqlWs);
export const PIXEL_LOADER_WS_URL: string = envOrFallback(
  env.PIXEL_LOADER_WS_ENDPOINT,
  config.pixelLoaderWs
);
export const ASR_PLEX_WS_URL: string = envOrFallback(env.ASR_PLEX_WS_URL, config.asrPlexWs);

export const DEPLOY_ENV: string = config.deployEnvironment;
export const NVOQ_WS_URL: string = envOrFallback(env.NVOQ_WS_URL, config.nvoqWs);
export const NVOQ_WS_MAXIMUM_CONNECTIONS: number = envOrFallback(
  env.NVOQ_WS_MAXIMUM_CONNECTIONS,
  config.nvoqWsMaximumConnections
);
export const SEGMENT_APP_ID = config.segmentAppId;
export const SPLITIO_API_KEY = config.splitioApiKey;
export const REPORTER_DEBUG: string = envOrFallback(env.REPORTER_DEBUG, 'false');
export const DATADOG_APP_VERSION: string = env.GIT_TAG ?? '?.?.?';
